<template>
  <div>
    <!-- Calendário (mesmo que já apresentado anteriormente) -->
    <h2 class="text-base font-semibold text-gray-900">Visitas em Andamento</h2>
    <div class="lg:grid lg:grid-cols-12 lg:gap-x-16">
      <!-- Seção do Calendário -->
      <div class="mt-10 text-center lg:col-start-8 lg:col-end-13 lg:row-start-1 lg:mt-9 xl:col-start-9">
        <div class="flex items-center text-gray-900">
          <button
            type="button"
            @click="prevMonth"
            class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
          >
            <span class="sr-only">Mês Anterior</span>
            <ChevronLeftIcon class="w-5 h-5" aria-hidden="true" />
          </button>
          <div class="flex-auto text-sm font-semibold">{{ currentMonthName }}</div>
          <button
            type="button"
            @click="nextMonth"
            class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
          >
            <span class="sr-only">Próximo Mês</span>
            <ChevronRightIcon class="w-5 h-5" aria-hidden="true" />
          </button>
        </div>
        <!-- Cabeçalho dos dias da semana -->
        <div class="mt-6 grid grid-cols-7 text-xs text-gray-500">
          <div>Segunda</div>
          <div>Terça</div>
          <div>Quarta</div>
          <div>Quinta</div>
          <div>Sexta</div>
          <div>Sábado</div>
          <div>Domingo</div>
        </div>
        <!-- Grid de dias (apenas dias do mês atual) -->
        <div class="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200">
          <button
            v-for="(day) in days"
            :key="day.date"
            type="button"
            :class="dayClasses()"
            @click="selectDay(day)"
          >
            <time :datetime="day.date" :class="timeClasses(day)">
              {{ getDayNumber(day.date) }}
            </time>
          </button>
        </div>
        <button
         @click="filterOpen"
          type="button"
          class="mt-8 w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-600"
        >
          Filtro
        </button>
      </div>

      <!-- Lista de Reuniões (Visitas em Andamento) -->
      <ol class="mt-4 divide-y divide-gray-100 text-sm lg:col-span-7 xl:col-span-8">
        <li
          v-for="meeting in listingData"
          :key="meeting.id"
          class="relative flex gap-x-6 py-6 odd:bg-gray-200 even:bg-white"
        >
        <div class="flex-auto">
            <!-- Usando flex wrap sem forçar full width -->
            <dl class="flex flex-wrap gap-4 text-sm text-gray-500">
              <!-- Cada campo ocupa espaço igual, mas com um tamanho mínimo para manter o layout -->
              <div class="flex-1 min-w-[200px]">
                <span class="font-semibold text-gray-900">NOME:</span>
                {{ displayOrDefault(meeting.segurado) }}
              </div>
              <div class="flex-1 min-w-[200px]">
                <span class="font-semibold text-gray-900">IDADE:</span>
                {{ displayOrDefault(meeting.idade) }}
              </div>
              <div class="flex-1 min-w-[200px] flex items-center gap-1">
                <CalendarIcon class="w-5 h-5 text-gray-400" />
                <span class="font-semibold text-gray-900">DATA DE CONTATO:</span>
                {{ displayOrDefault(meeting.data_agendamento) }}
              </div>
              <div class="flex-1 min-w-[200px] flex items-center gap-1">
                <CalendarIcon class="w-5 h-5 text-gray-400" />
                <span class="font-semibold text-gray-900">DATA RETORNO:</span>
                {{ displayOrDefault(meeting.data_retorno) }}
              </div>
              <div class="flex-1 min-w-[200px] flex items-center gap-1">
                <CalendarIcon class="w-5 h-5 text-gray-400" />
                <span class="font-semibold text-gray-900">DATA DE VISITA:</span>
                {{ displayOrDefault(meeting.data_visita) }}
              </div>
              <div class="flex-1 min-w-[200px] flex items-center gap-1">
                <MapPinIcon class="w-5 h-5 text-gray-400" />
                <span class="font-semibold text-gray-900">ENDEREÇO:</span>
                <a
                  :href="getGoogleMapsLink(displayOrDefault(meeting.endereco))"
                  target="_blank"
                  class="text-indigo-600 hover:underline"
                >
                  {{ displayOrDefault(meeting.endereco) }}
                </a>
              </div>
              <div class="flex-1 min-w-[200px]">
                <span class="font-semibold text-gray-900">AGENDAMENTO:</span>
                {{ displayOrDefault(meeting.agendamento) }}
              </div>
              <div class="flex-1 min-w-[200px]">
                <span class="font-semibold text-gray-900">AGENDADOR:</span>
                {{ displayOrDefault(meeting.agendador_email) }}
              </div>
              <div class="flex-1 min-w-[200px]">
                <span class="font-semibold text-gray-900">CONSULTOR:</span>
                {{ displayOrDefault(meeting.consultor_email) }}
              </div>
              <div class="flex-1 min-w-[200px]">
                <span class="font-semibold text-gray-900">STATUS:</span>
                {{ displayOrDefault(meeting.status) }}
              </div>
              <div class="flex-1 min-w-[200px]">
                <span class="font-semibold text-gray-900">STATUS DO CONTATO:</span>
                {{ displayOrDefault(meeting.status_contato) }}
              </div>
              <div class="flex-1 min-w-[200px]">
                <span class="font-semibold text-gray-900">STATUS COMERCIAL:</span>
                {{ displayOrDefault(meeting.status_commercial) }}
              </div>
              <!-- Anexos da Visita -->
              <div
                v-if="meeting.anexos && meeting.anexos.length > 0"
                class="w-full mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded-md shadow-sm"
              >
                <p class="font-semibold text-indigo-700 mb-2">Anexos:</p>
                <ul class="space-y-1 text-sm">
                  <li
                    v-for="(anexo, index) in meeting.anexos"
                    :key="index"
                    class="flex items-center justify-between bg-white p-2 rounded border border-gray-200 shadow-sm"
                  >
                    <span class="text-gray-700 truncate max-w-[70%]">{{ anexo.nome }}</span>
                    <a
                      :href="anexo.url"
                      target="_blank"
                      class="text-indigo-600 hover:underline text-sm font-medium"
                    >
                      Download
                    </a>
                  </li>
                </ul>
              </div>
            </dl>
          </div>  
          <div class="flex items-center justify-center pr-4">
            <!-- Se já chegou no destino, botão só redireciona -->
            <button
              v-if="meeting.chegou_no_destino"
              @click="abrirFormulario(meeting)"
              type="button"
              class="h-32 min-w-[120px] bg-sky-500 hover:bg-sky-600 focus:ring-sky-400 text-white text-sm font-semibold px-4 rounded-r-md focus:outline-none focus:ring-2 focus:ring-offset-2"
            >
              Checar Formulário
            </button>

            <!-- Se a visita está em andamento -->
            <button
              v-else-if="meeting.status_visita === 'em andamento'"
              @click="verificaLocalizacaoEInicia(meeting)"
              type="button"
              class="h-32 min-w-[120px] bg-green-500 hover:bg-green-600 focus:ring-green-400 text-white text-sm font-semibold px-4 rounded-r-md focus:outline-none focus:ring-2 focus:ring-offset-2"
            >
              Retomar Visita
            </button>

            <!-- Caso contrário, iniciar visita -->
            <button
              v-else
              @click="verificaLocalizacaoEInicia(meeting)"
              type="button"
              class="h-32 min-w-[120px] bg-indigo-600 hover:bg-indigo-500 focus:ring-indigo-600 text-white text-sm font-semibold px-4 rounded-r-md focus:outline-none focus:ring-2 focus:ring-offset-2"
            >
              Iniciar Visita
            </button>
          </div>
        </li>
      </ol>
      <VisitasModal v-if="selectedVisitId" 
        :id="selectedEntityId"
        :visitaDependente="visitaDependente"
        :visit="visit"
        :visitId="selectedVisitId" 
        :checked="checkedStatus"
        @form-send="editConfirmation"
        ref="visitModal" 
      />
      <!-- Paginação da Lista de Reuniões -->
      <div class="flex items-center justify-between mt-4 lg:col-span-7 xl:col-span-8">
        <!-- Botão Anterior -->
        <button
          @click="previousPage" v-if="previous"
          class="rounded-md bg-customPurple px-3 py-1 text-sm font-semibold text-white shadow-sm hover:bg-sienaBlue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
        >
          Anterior
        </button>
        <!-- Campo para ir a uma página específica -->
        <div class="flex items-center gap-2">
          <input
            type="number"
            v-model.number="pageNumber"
            min="1"
            :max="totalPages"
            class="border border-gray-300 rounded-md px-2 py-1 text-sm"
            placeholder="Ir para..."
          />
          <button
            @click="goToPage"
            class="flex-shrink rounded-md bg-customPurple px-2 py-1 text-center text-sm font-semibold text-white shadow-sm hover:bg-sienaBlue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Ir
          </button>
        </div>
        <!-- Botão Próximo -->
        <button
          @click="nextPage" v-if="next"
          class="rounded-md bg-customPurple px-3 py-1 text-sm font-semibold text-white shadow-sm hover:bg-sienaBlue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
        >
          Próximo
        </button>
      </div>
    </div>
  </div>
</template>

<script>

import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MapPinIcon,
} from "@heroicons/vue/20/solid";
import VisitasModal from "../visitas/VisitasModal.vue";
import Swal from "sweetalert2";

export default {
  name: "CalendarComponent",
  components: {
    CalendarIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    MapPinIcon,
    VisitasModal
  },
  data() {
    const today = new Date();
    return {
      currentDate: new Date(today.getFullYear(), today.getMonth(), 1),
      selectedDate: null,
      selectedEntityId: null,
      visitaDependente: null,
      selectedVisitId: null,
      checkedStatus: false,
      pageNumber: 1,
      visit:{}
    };
  },
  props:{
    listingData: {
      type: Array,
      required: true
    },
    totalPages: {
      type: Number,
      required: true,
    },
    previous: {
      type: String,
      required: true,
    },
    next: {
      type: String,
      required: true,
    },
    currentPage: {
      type: Number,
      required: true,
    },
    totalRecords: {
      type: Number,
      required: true,
    },
  },

  computed: {
    // Nome do mês corrente
    currentMonthName() {
      const monthNames = [
        "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
        "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
      ];
      return `${monthNames[this.currentDate.getMonth()]} ${this.currentDate.getFullYear()}`;
    },
    // Gera apenas os dias do mês atual (para o calendário)
    days() {
      const days = [];
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      const firstDayOfMonth = new Date(year, month, 1).getDay(); // Qual dia da semana começa (0 = Domingo, 1 = Segunda, ...)
      const lastDayOfMonth = new Date(year, month + 1, 0);
      const numDaysInMonth = lastDayOfMonth.getDate();

      // Adiciona os espaços vazios antes do primeiro dia do mês
      for (let i = 0; i < firstDayOfMonth; i++) {
        days.push({
          date: null,
          isCurrentMonth: false,
        });
      }

      // Adiciona os dias reais do mês
      for (let i = 1; i <= numDaysInMonth; i++) {
        const dayDate = new Date(year, month, i);
        days.push({
          date: dayDate.toISOString().split("T")[0],
          isCurrentMonth: true,
          isToday: this.isToday(dayDate),
          isHoliday: this.isHoliday(dayDate),
          isSelected: this.selectedDate === dayDate.toISOString().split("T")[0],
        });
      }

      return days;
    }
  },
  methods: {

    async verificaLocalizacaoEInicia(visit) {
      console.log(visit);
      const liberado = await this.checkLocalizacaoAntesDeIniciar();
      if (liberado) {
        this.startVisit(visit); // seu método já existente
      }
    },
    async checkLocalizacaoAntesDeIniciar() {
      return new Promise((resolve) => {
        if (!navigator.geolocation) {
          alert('Seu navegador não suporta geolocalização.');
          return resolve(false);
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            console.log('Latitude:', position.coords.latitude);
            console.log('Longitude:', position.coords.longitude);
            resolve(true); // ou passar o position pra frente se quiser
          },
          (error) => {
           switch (error.code) {
            case error.PERMISSION_DENIED:
            case error.POSITION_UNAVAILABLE:
              Swal.fire({
                icon: 'error',
                title: 'Localização Desativada',
                text: 'Ative a localização do seu dispositivo e recarregue a página para continuar.',
                confirmButtonColor: '#3085d6',
              });
              break;
            case error.TIMEOUT:
              Swal.fire({
                icon: 'error',
                title: 'Tempo Excedido',
                text: 'Não foi possível obter sua localização a tempo. Tente novamente.',
                confirmButtonColor: '#3085d6',
              });
              break;
            default:
              Swal.fire({
                icon: 'error',
                title: 'Erro Desconhecido',
                text: 'Não foi possível acessar sua localização.',
                confirmButtonColor: '#3085d6',
              });
          }
          resolve(false);
          },
          { timeout: 5000 }
        );
      });
    },

    displayOrDefault(value) {
      return value !== null ? value : "não definido";
    },
    // Verifica se a data passada corresponde a hoje
    isToday(date) {
      const today = new Date();
      return (
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate()
      );
    },
    // Considera sábado (6) e domingo (0) como feriados (ajuste se necessário)
    isHoliday(date) {
      const day = date.getDay();
      return day === 0 || day === 6;
    },
    startVisit(visit) {
      const meeting = this.listingData.find(m => m.id === visit.id);
      this.selectedVisitId = visit.id;
      console.log(`visita id do start ta aqui ${this.selectedVisitId }`)
      this.visit = visit;
      this.visitaDependente = meeting.visita_dependente;
      if (meeting.visita_dependente) {
        this.selectedEntityId = meeting.dependente_id;
      } else {
        this.selectedEntityId = meeting.segurado_id;
      }
      this.checkedStatus = meeting.chegou_no_destino;
      this.$nextTick(() => {
        if (this.$refs.visitModal?.openModal) {
          this.$refs.visitModal.openModal();
        } else {
          console.warn('visitModal ainda não montou');
        }
      });
    },
    abrirFormulario(visit){
      const meeting = this.listingData.find(m => m.id === visit.id);
      this.selectedVisitId = visit.id;
      console.log(`visita id do abrir ta aqui ${this.selectedVisitId }`)
      this.visit = visit;
      this.visitaDependente = meeting.visita_dependente;
      if (meeting.visita_dependente) {
        this.selectedEntityId = meeting.dependente_id;
      } else {
        this.selectedEntityId = meeting.segurado_id;
      }
      this.checkedStatus = meeting.chegou_no_destino;
      this.$nextTick(() => {
        if (this.$refs.visitModal?.openModal) {
          this.$refs.visitModal.openModal();
        } else {
          console.warn('visitModal ainda não montou');
        }
      });
    },
    // Retorna o número do dia (sem zero à esquerda)
    getDayNumber(dateStr) {
      if (!dateStr) return ""; // Retorna string vazia se a data for null para evitar erro
      const parts = dateStr.split("-");
      return parts[2].startsWith("0") ? parts[2].substring(1) : parts[2];
    },
    // Navegação do calendário
    nextMonth() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      this.currentDate = new Date(year, month + 1, 1);
      this.selectedDate = null;
    },
    prevMonth() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      this.currentDate = new Date(year, month - 1, 1);
      this.selectedDate = null;
    },
    // Seleciona um dia do calendário e emite para o pai
    selectDay(day) {
      this.selectedDate = day.date;
      this.$emit("daySelected",{data_visita: day.date});
    },
    // Link para o Google Maps com base no endereço
    getGoogleMapsLink(location) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
    },
    // Funções de paginação para a lista de reuniões
    previousPage() {
      this.$emit('previous-page', this.previous);
    },
    nextPage() {
      this.$emit('next-page', this.next);
    },
    goToPage() {
      this.$emit('go-to-page', this.pageNumber);
    },
    editConfirmation(id){
      this.$emit('edit-confirmation',id);
    },
    filterOpen(){
      this.$emit('filter');
    },
    // Função para as classes do <button> do dia (opcional, sem uso dos parâmetros)
    dayClasses() {
      return ["py-1.5", "hover:bg-gray-100", "focus:z-10"];
    },
    // Classes para o elemento <time> que exibe o número do dia, com destaque se selecionado
    timeClasses(day) {
      let classes = [
        "mx-auto",
        "flex",
        "items-center",
        "justify-center",
        "rounded-full",
        "w-8",
        "h-8",
      ];
      if (day.isSelected) {
        classes.push("bg-indigo-600", "text-white");
      } else {
        classes.push("text-gray-900");
      }
      return classes;
    },
  },
};
</script>
